import fetchData, { Methods } from '@/utils/api';
import { APIENDPOINT_AVATAR, APIENDPOINT_COUNTERPARTIES } from '@/constants/api_endpoints';
import {
  CheckCounterpartyVatResponse,
  CounterpartyActivityResponse,
  CounterpartyResponse,
  CounterpartyStatusFilter,
  CounterpartyType,
  CreateOrUpdateCounterpartyData,
  GetCounterpartiesAvailabilityResponse,
  GetCounterpartyDocumentsResponse,
  GetPaginatedCounterpartiesResponse,
  RemoveCounterpartyTypeResponse
} from '@/types/counterparty';
import { DocumentStatus } from '@/types/document';

// The company is never sent: the API reads it from the session.

export async function getCounterpartiesAvailability(): Promise<GetCounterpartiesAvailabilityResponse> {
  return fetchData(
    Methods.GET,
    `${APIENDPOINT_COUNTERPARTIES}/availability`,
    undefined,
    undefined,
    false
  );
}

export async function getPaginatedSuppliers(
  page: number,
  limit: number,
  status: CounterpartyStatusFilter,
  search?: string
): Promise<GetPaginatedCounterpartiesResponse> {
  return fetchData(Methods.GET, APIENDPOINT_COUNTERPARTIES, {
    section: 'suppliers',
    page,
    limit,
    status,
    ...(search ? { search } : {})
  });
}

export async function getCounterparty(id: string): Promise<CounterpartyResponse> {
  return fetchData(Methods.GET, `${APIENDPOINT_COUNTERPARTIES}/${id}`);
}

export async function createCounterparty(
  counterparty: CreateOrUpdateCounterpartyData
): Promise<CounterpartyResponse> {
  return fetchData(Methods.POST, APIENDPOINT_COUNTERPARTIES, counterparty);
}

export async function updateCounterparty(
  id: string,
  counterparty: Partial<CreateOrUpdateCounterpartyData>
): Promise<CounterpartyResponse> {
  return fetchData(Methods.PUT, `${APIENDPOINT_COUNTERPARTIES}/${id}`, counterparty);
}

export async function setCounterpartyActive(
  id: string,
  type: CounterpartyType,
  active: boolean
): Promise<CounterpartyResponse> {
  return fetchData(
    Methods.PATCH,
    `${APIENDPOINT_COUNTERPARTIES}/${id}/${active ? 'activate' : 'deactivate'}`,
    { type }
  );
}

export async function removeCounterpartyType(
  id: string,
  type: CounterpartyType
): Promise<RemoveCounterpartyTypeResponse> {
  return fetchData(Methods.DELETE, `${APIENDPOINT_COUNTERPARTIES}/${id}/type/${type}`);
}

export async function getCounterpartyActivity(id: string): Promise<CounterpartyActivityResponse> {
  return fetchData(Methods.GET, `${APIENDPOINT_COUNTERPARTIES}/${id}/activity`, {
    view: 'supplier'
  });
}

export async function checkCounterpartyVat(
  id: string,
  vatNumber?: string
): Promise<CheckCounterpartyVatResponse> {
  return fetchData(
    Methods.POST,
    `${APIENDPOINT_COUNTERPARTIES}/${id}/vat_check`,
    vatNumber ? { vat_number: vatNumber } : {}
  );
}

export async function updateCounterpartyAvatar(
  id: string,
  avatarBase64Data: string
): Promise<CounterpartyResponse> {
  return fetchData(Methods.POST, `${APIENDPOINT_COUNTERPARTIES}/${id}${APIENDPOINT_AVATAR}`, {
    file: avatarBase64Data
  });
}

export async function getCounterpartyDocuments(
  id: string,
  page: number,
  limit: number,
  period: { year: number; month: number } | null,
  statuses: DocumentStatus[]
): Promise<GetCounterpartyDocumentsResponse> {
  return fetchData(Methods.GET, `${APIENDPOINT_COUNTERPARTIES}/${id}/documents`, {
    page,
    limit,
    ...(period ? { year: period.year, month: period.month } : {}),
    statuses: statuses.join(',')
  });
}
